import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import { IMAGE_PRESETS, type ImageKind } from '../lib/image-presets';
import { ACCEPTED_IMAGE_TYPES, ImageUploadError, uploadImage } from '../lib/image-upload';
import { haptic } from '../lib/telegram';
import { ImageCropper } from './image-cropper';

/**
 * Загрузка картинки: выбор файла → кадрирование под пресет → сжатие в браузере
 * → отправка в API. Сохранение самой привязки делает вызывающий экран —
 * компонент не знает, куда именно она крепится.
 */
export function ImageField({
  label,
  hint,
  kind,
  url,
  shape = 'wide',
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  kind: ImageKind;
  url: string | null;
  shape?: 'wide' | 'circle';
  disabled?: boolean;
  onChange: (imageId: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Object URL держит файл в памяти, пока открыт кроппер.
  useEffect(() => () => releaseSource(source), [source]);

  const pick = (file: File | undefined) => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Это не изображение');
      return;
    }

    setError(null);
    setSource(URL.createObjectURL(file));
  };

  const upload = async (blob: Blob) => {
    setPending(true);
    setError(null);

    try {
      const image = await uploadImage(blob, kind);
      onChange(image.id);
      haptic('success');
      closeCropper();
    } catch (failure) {
      haptic('error');
      setError(
        failure instanceof ImageUploadError || failure instanceof ApiError
          ? failure.message
          : 'Не удалось загрузить картинку',
      );
    } finally {
      setPending(false);
    }
  };

  const closeCropper = () => {
    releaseSource(source);
    setSource(null);
    // Тот же файл должен выбираться повторно после отмены или ошибки.
    if (input.current) {
      input.current.value = '';
    }
  };

  return (
    <div className="image-field">
      <div className="image-field__head">
        <span className="field__label">{label}</span>
        {url && !pending && (
          <button
            type="button"
            className="image-field__clear"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            Убрать
          </button>
        )}
      </div>
      <button
        type="button"
        className={`image-field__frame image-field__frame--${shape} ${url ? 'has-image' : ''}`}
        disabled={disabled || pending}
        onClick={() => input.current?.click()}
      >
        {url ? <img src={url} alt="" /> : <span>{pending ? 'Загружаем…' : '+ Добавить'}</span>}
        {url && pending && <span className="image-field__overlay">Загружаем…</span>}
      </button>
      {!error && <span className="field__hint">{hint ?? sizeHint(kind)}</span>}
      {/* Пока открыт кроппер, ошибку показывает он: там же и кнопка повтора. */}
      {error && !source && <span className="field__error">{error}</span>}
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        hidden
        onChange={(event) => pick(event.target.files?.[0])}
      />

      {source && (
        <ImageCropper
          source={source}
          kind={kind}
          pending={pending}
          error={error}
          onCancel={closeCropper}
          onCrop={(blob) => void upload(blob)}
        />
      )}
    </div>
  );
}

function sizeHint(kind: ImageKind): string {
  const preset = IMAGE_PRESETS[kind];
  return preset.aspect === null
    ? `До ${preset.maxSide} px по длинной стороне`
    : `Кадрируется под ${describeAspect(preset.aspect)}`;
}

function describeAspect(aspect: number): string {
  if (Math.abs(aspect - 1) < 0.01) return 'квадрат';
  if (Math.abs(aspect - 4 / 3) < 0.01) return '4:3';
  return '16:6';
}

function releaseSource(source: string | null): void {
  if (source) {
    URL.revokeObjectURL(source);
  }
}
